import { getDataWithRetry, mapPagedResult, sendWithoutBody } from '../fetchUtils';
import {
  isValidTrainersResponse,
  isValidTrainerResponse,
} from '../apiValidators';
import { apiBaseUrl } from '../apiConsts';
import { getDefaultError } from '../../utils';

import type { Trainer, WithOptionalId, PagedResponse } from '../../types';

import type { FetchResult, FetchSuccess } from '../fetchUtils';

const createSuccessResult = <T>(data: T): FetchSuccess<T> => ({
  success: true,
  data,
  status: 200,
  durationMs: 0,
});

// NOTE: Developer-only impersonation helper. Do not expose to general UI flows.
export const fetchAllTrainers = async (): Promise<FetchResult<Trainer[]>> => {
  const response = await getDataWithRetry<PagedResponse<Trainer>>(
    `${apiBaseUrl}trainers?page=0&size=1000&sort=firstName`,
    {},
    isValidTrainersResponse,
  );
  return mapPagedResult(response);
};

export const fetchTrainerById = (
  trainerId: string,
): Promise<FetchResult<Trainer | null>> => {
  return getDataWithRetry(
    `${apiBaseUrl}trainers/${trainerId}`,
    {},
    isValidTrainerResponse,
  );
};

export const fetchTrainersByIds = async (
  trainerIds: string[],
): Promise<FetchResult<Trainer[]>> => {
  if (trainerIds.length === 0) {
    return createSuccessResult<Trainer[]>([]);
  }
  const response = await getDataWithRetry<PagedResponse<Trainer>>(
    `${apiBaseUrl}trainers/getByIds?page=0&size=1000&sort=firstName`,
    {
      headers: { 'content-type': 'application/json' },
      method: 'POST',
      body: JSON.stringify(trainerIds),
    },
    isValidTrainersResponse,
  );
  return mapPagedResult(response);
};

export const createNewTrainer = async (
  trainer: WithOptionalId<Trainer>,
  gymId: string,
): Promise<FetchResult<Trainer | null>> => {
  if (!gymId) {
    return {
      data: null,
      success: false,
      error: getDefaultError('No Gym Provided'),
    };
  }
  const createResponse = await getDataWithRetry<Trainer | null>(
    `${apiBaseUrl}trainers`,
    {
      headers: { 'content-type': 'application/json' },
      method: 'POST',
      body: JSON.stringify(trainer),
    },
    isValidTrainerResponse,
  );

  if (!createResponse.success || !createResponse.data) {
    return {
      success: false,
      data: null,
      error:
        createResponse.error ?? getDefaultError('Failed to create trainer'),
      status: createResponse.status,
      durationMs: createResponse.durationMs,
    };
  }

  const associateResult = await sendWithoutBody(
    `${apiBaseUrl}gyms/${gymId}/associate/trainers`,
    {
      headers: { 'content-type': 'application/json' },
      method: 'POST',
      body: JSON.stringify([createResponse.data.id]),
    },
  );

  if (!associateResult.success) {
    return associateResult;
  }

  return {
    ...createResponse,
    data: {
      ...createResponse.data,
      gymIds: [gymId],
    },
  };
};

export const updateTrainerData = async (
  trainer: Trainer,
): Promise<FetchResult<Trainer | null>> => {
  if (!trainer?.id) {
    return {
      data: null,
      success: false,
      error: getDefaultError('No trainer id provided'),
    };
  }
  const updateResponse = await getDataWithRetry<Trainer | null>(
    `${apiBaseUrl}trainers`,
    {
      headers: { 'content-type': 'application/json' },
      method: 'PUT',
      body: JSON.stringify(trainer),
    },
    isValidTrainerResponse,
  );
  if (!updateResponse.success) {
    return updateResponse;
  }
  return updateResponse;
};

export const getGymTrainers = async (
  gymId: string,
): Promise<FetchResult<Trainer[]>> => {
  const response = await getDataWithRetry<PagedResponse<Trainer>>(
    `${apiBaseUrl}trainers/gym/${gymId}?page=0&size=1000&sort=firstName`,
    {},
    isValidTrainersResponse,
  );
  return mapPagedResult(response);
};
